import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiX, FiMaximize2, FiCloud, FiCode, FiDatabase, FiBriefcase } from "react-icons/fi";

export default function Experience() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedImage, setSelectedImage] = useState(null);

  const experiences = [
    {
      role: "AWS Cloud Consultant",
      company: "Sapphire Technocrats",
      duration: "Present",
      type: "Full-Time",
      icon: <FiCloud />,
      tech: ["AWS", "Terraform (IaC)", "Docker", "Kubernetes", "ECS/EKS", "CI/CD", "EC2 Auto Scaling", "VPC Networking", "IAM RBAC", "CloudWatch"],
      image: "/sapphire-technocrats-logo.png",
      details:
        "Architecting fault-tolerant, highly available cloud environments utilizing the AWS Well-Architected Framework. Designing CI/CD pipelines with Docker and ECS/EKS to automate deployments. Provisioning infrastructure via Terraform and enforcing IAM access control.",
    },
    {
      role: "Full Stack Developer Intern",
      company: "Infosys Springboard 6.0",
      duration: "Feb 2025 - Apr 2025",
      type: "Internship",
      icon: <FiCode />,
      tech: ["React.js", "Node.js", "Express.js", "MongoDB", "REST APIs"],
      image: "/Infosys_Internship_Certificate_page-0001.jpg",
      details:
        "Engineered a dynamic service marketplace platform connecting customers with providers. Developed robust backend APIs, implemented JWT authentication, and built responsive UI components utilizing the MERN stack for optimal performance.",
    },
    {
      role: "SQL Developer Intern",
      company: "Elevate Labs (Remote)",
      duration: "Jun 2025 - Jul 2025",
      type: "Internship",
      icon: <FiDatabase />,
      tech: ["SQL", "MySQL", "Database Design", "Excel Analytics"],
      image: "/Alok_Kumar_SQL_Internship_certificate_page-0001.jpg",
      details:
        "Designed an optimized database schema for an Airline Reservation System ensuring relational integrity. Authored complex SQL queries, triggers, and stored procedures, improving query execution time by 20% alongside data visualization.",
    },
  ];

  const activeExp = experiences[activeIndex];

  return (
    <section
      id="experience"
      className="w-full min-h-screen py-24 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 relative overflow-hidden flex flex-col items-center justify-center"
    >
      {/* Background Glows (Matching Projects Section) */}
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-violet-500/10 rounded-full blur-[120px] pointer-events-none" />

      {/* Section Header */}
      <div className="text-center mb-16 px-6 relative z-10 w-full">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="inline-flex items-center justify-center p-3 bg-white dark:bg-gray-800/50 rounded-full mb-6 border border-gray-200 dark:border-gray-700 shadow-sm"
        >
          <FiBriefcase className="text-2xl text-cyan-500 mx-2" />
          <span className="text-sm font-bold tracking-widest text-gray-700 dark:text-gray-300 uppercase pr-4">Career</span>
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-gray-900 dark:text-white mb-6 tracking-tight"
        >
          Professional{" "}
          <span className="bg-gradient-to-r from-cyan-400 to-violet-500 bg-clip-text text-transparent">
            Experience
          </span>
        </motion.h2>
        <p className="text-gray-600 dark:text-gray-400 text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
          My professional journey including full-time cloud consulting roles and industry-level software engineering internships.
        </p>
      </div>

      {/* Interactive Dashboard Container */}
      <div className="w-full px-6 md:px-10 lg:px-16 2xl:px-24 relative z-10 flex flex-col lg:flex-row gap-8 lg:gap-12 2xl:gap-16 items-stretch max-w-[1800px] mx-auto">

        {/* LEFT: Clickable Project Selector Menu */}
        <div className="w-full lg:w-1/3 xl:w-1/4 flex flex-col relative">
          {/* Vertical Track Line */}
          <div className="absolute left-[27px] top-6 bottom-6 w-1 bg-gray-200 dark:bg-gray-800 rounded-full hidden md:block" />

          <div className="flex flex-row lg:flex-col overflow-x-auto lg:overflow-visible pb-6 lg:pb-0 gap-2 lg:gap-0 custom-scrollbar">
            {experiences.map((exp, index) => {
              const isActive = activeIndex === index;
              return (
                <div
                  key={index}
                  onClick={() => setActiveIndex(index)}
                  className={`relative flex-shrink-0 lg:flex-shrink w-[280px] lg:w-auto md:pl-16 p-4 md:py-6 cursor-pointer group transition-all duration-300 rounded-2xl lg:rounded-none ${isActive ? "bg-white/60 dark:bg-gray-800/60 lg:bg-transparent lg:dark:bg-transparent shadow-sm lg:shadow-none border border-cyan-200 dark:border-cyan-800 lg:border-none opacity-100" : "opacity-60 hover:opacity-100"}`}
                >
                  {/* Glowing Node (Hidden on mobile row view, shown on desktop vertical view) */}
                  <div className={`hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full border-4 items-center justify-center transition-all duration-500 z-10 
                    ${isActive ? "bg-gradient-to-br from-cyan-400 to-violet-500 border-white dark:border-gray-900 shadow-[0_0_20px_rgba(34,211,238,0.5)] scale-110"
                      : "bg-gray-200 dark:bg-gray-800 border-gray-50 dark:border-gray-950 group-hover:border-cyan-400/50"}`}
                  >
                    <span className={`text-xl ${isActive ? "text-white" : "text-gray-500 dark:text-gray-400"}`}>
                      {exp.icon}
                    </span>
                  </div>

                  {/* Title & Company */}
                  <div className="flex flex-col">
                    <span className={`text-xs font-bold tracking-wider mb-1 uppercase transition-colors ${isActive ? "text-cyan-500" : "text-gray-500"}`}>
                      {exp.company}
                    </span>
                    <h3 className={`text-lg md:text-xl font-bold transition-colors ${isActive ? "text-gray-900 dark:text-white" : "text-gray-700 dark:text-gray-400"}`}>
                      {exp.role}
                    </h3>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* RIGHT: Expandable Dashboard Detail Panel */}
        <div className="w-full lg:w-2/3 xl:w-3/4 flex">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeIndex}
              initial={{ opacity: 0, x: 20, filter: "blur(8px)" }}
              animate={{ opacity: 1, x: 0, filter: "blur(0px)" }}
              exit={{ opacity: 0, x: -20, filter: "blur(8px)" }}
              transition={{ duration: 0.4, ease: "easeInOut" }}
              className="w-full bg-white/70 dark:bg-gray-800/50 backdrop-blur-xl border border-gray-200 dark:border-gray-700 rounded-[2rem] shadow-2xl flex flex-col h-full relative overflow-hidden"
            >
              {/* Subtle inner top glow */}
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-cyan-400 to-violet-500 opacity-50" />

              {/* Browser Mockup Image Container */}
              <div className="relative w-full border-b border-gray-200 dark:border-gray-700/50 bg-gray-50 dark:bg-gray-900">
                {/* Browser Bar */}
                <div className="w-full h-10 bg-gray-200/50 dark:bg-gray-950/50 flex items-center px-4 gap-2 backdrop-blur-sm z-10 border-b border-gray-200 dark:border-gray-800">
                  <div className="w-3 h-3 rounded-full bg-[#FF5F56] shadow-sm"></div>
                  <div className="w-3 h-3 rounded-full bg-[#FFBD2E] shadow-sm"></div>
                  <div className="w-3 h-3 rounded-full bg-[#27C93F] shadow-sm"></div>
                  <div className="ml-2 flex-grow max-w-sm h-5 bg-white/50 dark:bg-gray-800/50 rounded-md flex items-center px-3 text-xs text-gray-400 dark:text-gray-500 font-mono truncate">
                    verified-certificate://{activeExp.company.replace(/\s+/g, '-').toLowerCase()}
                  </div>
                </div>

                {/* Main Image */}
                <div
                  className="relative overflow-hidden cursor-pointer aspect-video md:aspect-[21/9] lg:aspect-video xl:aspect-[21/9] group bg-white dark:bg-gray-900 flex items-center justify-center"
                  onClick={() => setSelectedImage(activeExp.image)}
                >
                  <img
                    src={activeExp.image}
                    alt={`${activeExp.role} at ${activeExp.company} by Alok Kumar`}
                    className="w-full h-full object-cover md:object-contain object-center transition-transform duration-700 group-hover:scale-[1.02]"
                    loading="lazy"
                  />
                  {/* Expand Overlay */}
                  <div className="absolute inset-0 bg-gray-900/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center backdrop-blur-sm">
                    <div className="bg-cyan-500 text-white p-4 rounded-full shadow-2xl transform scale-75 group-hover:scale-100 transition-transform duration-300 flex items-center gap-2 font-bold px-6">
                      <FiMaximize2 className="text-xl" /> View Document
                    </div>
                  </div>
                </div>
              </div>

              {/* Content Panel */}
              <div className="p-8 md:p-10 flex flex-col flex-grow">
                <div className="flex flex-col xl:flex-row xl:items-start justify-between gap-6 mb-6">
                  <div>
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-cyan-100/50 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 text-sm font-semibold mb-4 border border-cyan-200 dark:border-cyan-800">
                      {activeExp.icon} {activeExp.type}
                    </div>
                    <h3 className="text-3xl lg:text-4xl font-extrabold text-gray-900 dark:text-white leading-tight">
                      {activeExp.role}
                    </h3>
                  </div>

                  {/* Desktop Duration/Company */}
                  <div className="flex flex-col xl:items-end">
                    <span className="text-xl font-bold text-violet-600 dark:text-violet-400">
                      {activeExp.company}
                    </span>
                    <span className="text-sm font-semibold text-gray-500 dark:text-gray-400 mt-1 bg-gray-100 dark:bg-gray-800 px-3 py-1 rounded-md">
                      {activeExp.duration}
                    </span>
                  </div>
                </div>

                <p className="text-gray-700 dark:text-gray-300 text-lg leading-relaxed mb-8 flex-grow">
                  {activeExp.details}
                </p>

                {/* Tech Stack Tags */}
                <div>
                  <h4 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-4 border-b border-gray-200 dark:border-gray-700 pb-2">
                    Core Technologies
                  </h4>
                  <div className="flex flex-wrap gap-2.5">
                    {activeExp.tech.map((tech, idx) => (
                      <span
                        key={idx}
                        className="text-sm font-semibold px-4 py-2 rounded-lg bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-700 shadow-sm hover:border-cyan-500/50 hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors cursor-default"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>

              </div>
            </motion.div>
          </AnimatePresence>
        </div>

      </div>

      {/* Full-Screen Image Modal */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-900/95 backdrop-blur-2xl flex items-center justify-center z-[100] p-4 sm:p-10"
            onClick={() => setSelectedImage(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="relative max-w-[95vw] lg:max-w-[80vw] w-auto max-h-[90vh] rounded-[1.5rem] overflow-hidden shadow-[0_0_60px_rgba(0,0,0,0.6)] bg-gray-950 border border-gray-800 flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="absolute top-4 right-4 z-50">
                <button
                  onClick={() => setSelectedImage(null)}
                  className="bg-black/60 hover:bg-cyan-500 backdrop-blur-lg border border-white/20 text-white rounded-full p-3 hover:scale-110 transition-all duration-300 shadow-xl"
                  aria-label="Close Modal"
                >
                  <FiX className="text-2xl" />
                </button>
              </div>
              <div className="w-full h-full max-h-[90vh] overflow-y-auto custom-scrollbar bg-gray-900 flex items-center justify-center p-2 md:p-8">
                <img
                  src={selectedImage}
                  alt="Expanded Project View"
                  className="w-full h-auto object-contain block max-h-[85vh] rounded-lg"
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
